import './Profile.scss'

import { useState, useEffect, useMemo } from 'react'
import { Col, Container, Row, Form, FloatingLabel, Button } from 'react-bootstrap'

import { isEmail } from '../../common/helpers/Strings'
import Error from '../../common/error/Error'
import { getUserPreferences, saveUserPreferences, verifyUsernameIsUnique } from '../../common/api/Users'
import { getAuthenticatedUser, REMBLE_PASSWORD_MIN_LENGTH } from '../../auth'
import PasswordRequirement from '../../auth/PasswordRequirement'
import AccountMenu from '../../common/navigation/AccountMenu'
import AccountDeleteNotice from '../AccountDeleteNotice'
import UserAssessmentModal from 'components/assessment/UserAssessmentModal'
import { updateUserPassword, updateUserProfile } from 'components/common/api/Profile'

const TYPE_PASSWORD = 'password'
const TYPE_TEXT = 'text'

/**
 * Simple Profile component. Used to display the user's profile details
 */
export default function Profile({ setReturnPath, setHeaderTitle, setShowToast, setToastMessage }) {

  // Loading and error state defaults
  const [loading, setLoading] = useState()
  const [error, setError] = useState()

  const user = useMemo(() => {
    const authUser = getAuthenticatedUser()
    return authUser
  }, [])

  // Name
  const [name, setName] = useState(user?.display_name)
  const [isValidName, setIsValidName] = useState(!!user?.display_name)

  // Email
  const [email, setEmail] = useState(user?.email)
  const [isValidEmail, setIsValidEmail] = useState(!!user?.email)

  // Password
  const passwordMinLength = REMBLE_PASSWORD_MIN_LENGTH
  const passwordMask = '*'.repeat(passwordMinLength)
  const [password, setPassword] = useState(passwordMask)
  const [isValidPassword, setIsValidPassword] = useState(true)
  const [passwordHasLength, setPasswordHasLength] = useState()
  const [passwordHasLowercase, setPasswordHasLowercase] = useState()
  const [passwordHasUppercase, setPasswordHasUppercase] = useState()
  const [passwordHasNumber, setPasswordHasNumber] = useState()
  const [passwordHasSymbol, setPasswordHasSymbol] = useState()

  // Password Confirmation
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [isValidPasswordConfirm, setIsValidPasswordConfirm] = useState(true)
  const [passwordType, setPasswordType] = useState(TYPE_PASSWORD)
  const [confirmPasswordType, setConfirmPasswordType] = useState(TYPE_PASSWORD)

  // User Preferences
  const [userPreferences, setUserPreferences] = useState()
  const [useAnonymously, setUseAnonymously] = useState(!!userPreferences?.track_user_id)

  /**
   * Effect to fetch the user's preferences
   */
  useEffect(() => {
    async function getUserPreferencesAsync() {
      const preferences = await getUserPreferences()
      setUserPreferences(preferences)
      setUseAnonymously(!!preferences.track_user_id)
    }

    getUserPreferencesAsync()
  }, [])

  const shouldShowPasswordConfirm = useMemo(() => {
    return password !== passwordMask
  }, [password, passwordMask])

  /**
   * Toggle helper function to swap input type between `text` and `password` on password field
   */
  const togglePasswordVisibility = () =>
    setPasswordType(passwordType === TYPE_PASSWORD ? TYPE_TEXT : TYPE_PASSWORD)

  /**
   * Toggle helper function to swap input type between `text` and `password` on password confirmation field
   */
  const toggleConfirmPasswordVisibility = () =>
    setConfirmPasswordType(
      confirmPasswordType === TYPE_PASSWORD ? TYPE_TEXT : TYPE_PASSWORD
    )

  /**
   * Helper function to see if the `email` provided is an existing user
   * @param {String} value The field's value
   */
  const existingUserCheck = async event => {
    // Return early if it's not a valid email
    if (!email || !isValidEmail) return

    // Let's verify that the username isn't already registered before we get too far
    setLoading(true)

    // Check for response type
    let isUnique = await verifyUsernameIsUnique(email)

    // Error communicating with the backend server
    if (isUnique.error) return setError(isUnique.error)

    isUnique = isUnique.valid
    setLoading(false)
  }

  /**
   * Sets and runs validation on `name` field
   * @param {String} value The field's value
   */
  const setAndValidateName = value => {
    const isValid = value.length >= 1

    setName(value)
    setIsValidName(isValid)
  }

  /**
   * Sets and runs validation on `email` field
   * @param {String} value The field's value
   */
  const setAndValidateEmail = value => {
    const isValid = isEmail(value)

    setEmail(value)
    setIsValidEmail(isValid)
  }
  /**
   * Sets and runs validation on `password` field. Ensures that the password matches the password policy requirements
   * Does password match our password policy?
   * 8 character min length
   * At least 1 uppercase char
   * At least 1 lowercase char
   * At least 1 number
   * At least 1 symbol (^$*.[]{}()? "!@#%&/\,><':;|_~`+=-)
   * @param {String} value The field's value
   */
  const setAndValidatePassword = value => {
    const hasLength = value.length > passwordMinLength - 1
    const hasUppercase = /[A-Z]/.test(value)
    const hasLowercase = /[a-z]/.test(value)
    const hasNumber = /[0-9]/.test(value)
    const hasSymbol = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(value)

    const isValid =
      hasLength && hasUppercase && hasLowercase && hasNumber && hasSymbol

    // If we update the password, let's update the confirmation
    if (passwordConfirm.length)
      setIsValidPasswordConfirm(value === passwordConfirm)

    setPassword(value)
    setPasswordHasLength(hasLength)
    setPasswordHasUppercase(hasUppercase)
    setPasswordHasLowercase(hasLowercase)
    setPasswordHasNumber(hasNumber)
    setPasswordHasSymbol(hasSymbol)
    setIsValidPassword(isValid)
  }

  const clearPwdValidation = (value) => {
    setPassword(value)
    setPasswordHasLength(undefined)
    setPasswordHasUppercase(undefined)
    setPasswordHasLowercase(undefined)
    setPasswordHasNumber(undefined)
    setPasswordHasSymbol(undefined)
    setIsValidPassword(true)
  }

  /**
   * Sets and runs validation on `passwordConfirm` field
   * @param {String} value The field's value
   */
  const setAndValidatePasswordConfirm = value => {
    const isValid = value === password

    setPasswordConfirm(value)
    setIsValidPasswordConfirm(isValid)
  }

  /**
   * Helper function to handle `blur` on `name` field
   */
  const handleNameBlur = (isEnterKey = false) => {
    // TODO: save user's updated name
  }

  /**
   * Helper function to handle `blur` on `email` field
   */
  const handleEmailBlur = async event => {
    // TODO: save user's updated email and go through verification
    // Log user out on email or password change, to refresh authentication
    await existingUserCheck()
  }

  /**
   * Helper function to handle `blur` on `password` field
   */
  const handlePasswordBlur = (isEnterKey = false) => {
    // TODO: show stuff
    // Log user out on email or password change, to refresh authentication
  }

  /**
   * Helper function to handle `blur` on `passwordConfirm` field
   */
  const handlePasswordConfirmBlur = (isEnterKey = false) => {
    // TODO: more stuff
  }

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Update header and return path
    setReturnPath(null)
    setHeaderTitle('Profile')
  }, [email, setReturnPath, setHeaderTitle])

  const handleUpdateProfile = async () => {
    setLoading(true)

    await updateUserProfile(name)
    setName(name)
    setLoading(false)
    setShowToast(true)
    setToastMessage(`The user's name was successfully updated.`)
  }

  const handleUpdatePassword = async () => {
    if (!isValidPassword || !isValidPasswordConfirm) return
    if (password === '' || passwordConfirm === '') return

    setLoading(true)

    await updateUserPassword(password)
    setLoading(false)
    setPassword(passwordMask)
    setPasswordConfirm('')
    setShowToast(true)
    setToastMessage('The password was successfully updated.')
  }

  return (
    <Container className='profile-container'>
      {/* Show errors if we have any */}
      <Error error={error} />
      <Row xs={1} md={2}>
        {/* Sidebar panel for larger screens */}
        <Col md={4} className='mobile-hidden account-sidebar'>
          <AccountMenu />
        </Col>
        <Col md={8}>
          <h1 className='title'>Your Profile</h1>

          <Form.Group controlId='formEmail' className='form-group visible mb-3'>
            <FloatingLabel controlId='floatingEmail' label='Your email address'>
              <Form.Control
                name='email'
                defaultValue={email}
                disabled={!!loading || true}
                size='lg'
                placeholder='Your email address'
                required
                type='email'
                onChange={e => setAndValidateEmail(e.currentTarget.value)}
                // Only show password field once we have a valid email and username
                onBlur={handleEmailBlur}
                isInvalid={!isValidEmail}
                isValid={email && isValidEmail}
              />
              <Form.Control.Feedback type='invalid'>
                Please provide a valid email address
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>

          <Form.Group controlId='formName' className='form-group visible mb-3'>
            <FloatingLabel
              controlId='floatingName'
              label='What should we call you?'
            >
              <Form.Control
                name='name'
                defaultValue={name}
                disabled={!!loading}
                size='lg'
                placeholder='What should we call you?'
                required
                type='text'
                onChange={e => setAndValidateName(e.currentTarget.value)}
                onBlur={() => handleNameBlur()}
                onKeyDown={e => e.key === 'Enter' && handleNameBlur(true)}
                isInvalid={!isValidName}
                isValid={name && isValidName}
              />
              <Form.Control.Feedback type='invalid'>
                Your name is required.
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>

          {user && user.display_name !== name && (
            <Row className='mb-3'>
              <Col>
                <Button
                  variant='primary'
                  className='w-100'
                  onClick={() => {
                    handleUpdateProfile()
                  }}
                >
                  Update my name
                </Button>
              </Col>
            </Row>
          )}

          <Form.Group
            controlId='formCurrentPassword'
            className='form-group visible mb-3 form-password'
            // hidden={true}
          >
            <FloatingLabel
              controlId='floatingCurrentPassword'
              label='Your current password'
            >
              <span
                className='visibility-toggle'
                onClick={() => togglePasswordVisibility()}
              >
                {passwordType === TYPE_PASSWORD ? (
                  <>
                    <i className='icon bi-eye-slash'></i>
                  </>
                ) : (
                  <>
                    <i className='icon bi-eye'></i>
                  </>
                )}
              </span>
              <Form.Control
                name='currentPassword'
                disabled={!!loading}
                size='lg'
                placeholder='Your current password'
                required
                type={passwordType}
                value={password}
                onChange={e => setAndValidatePassword(e.currentTarget.value)}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    clearPwdValidation(passwordMask)
                  }
                }}
                onFocus={(e) => {
                  if (e.target.value === passwordMask) {
                    clearPwdValidation('')
                  }
                }}
                // Only show password confirmation field once we have a valid password
                // onBlur={() => handlePasswordBlur()}
                // onKeyDown={e => e.key === 'Enter' && handlePasswordBlur(true)}
                isInvalid={!isValidPassword}
                isValid={password && isValidPassword}
              />
              <Form.Control.Feedback type='invalid'>
                Please provide a valid password
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>

          {/* Only show password if we're going to change it */}
          <Form.Group
            className='form-group'
            controlId='formPassword'
          >
            <FloatingLabel controlId='floatingPassword' label='Your password'>
              <span
                className='visibility-toggle'
                onClick={() => togglePasswordVisibility()}
              >
                {passwordType === TYPE_PASSWORD ? (
                  <i className='icon bi-eye-slash'></i>
                ) : (
                  <i className='icon bi-eye'></i>
                )}
              </span>
              <Form.Control
                name='password'
                disabled={!!loading}
                size='lg'
                placeholder='Your password'
                required
                type={passwordType}
                onChange={e => setAndValidatePassword(e.currentTarget.value)}
                // Only show password confirmation field once we have a valid password
                onBlur={() => handlePasswordBlur()}
                onKeyDown={e => e.key === 'Enter' && handlePasswordBlur(true)}
                isInvalid={!isValidPassword}
                isValid={password && isValidPassword}
              />
              <Form.Control.Feedback type='invalid'>
                Please provide a valid password
                <ul className='password-requirements'>
                  {PasswordRequirement(
                    passwordHasLength,
                    passwordHasLength
                      ? `${passwordMinLength} character min length.`
                      : `${passwordMinLength} character min length. Needs ${
                          passwordMinLength - password.length
                        } more.`
                  )}
                  {PasswordRequirement(
                    passwordHasUppercase,
                    'At least 1 uppercase letter'
                  )}
                  {PasswordRequirement(
                    passwordHasLowercase,
                    'At least 1 lowercase letter'
                  )}
                  {PasswordRequirement(passwordHasNumber, 'At least 1 number')}
                  {PasswordRequirement(
                    passwordHasSymbol,
                    'At least 1 special character (e.g., "&!.@", etc.)'
                  )}
                </ul>
              </Form.Control.Feedback>
              <Form.Control.Feedback>
                That's a good looking password. A secure password, keeps your
                account safe. 🙂
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>

          {/* Only show password confirmation once we have a valid password */}
          <Form.Group
            className={`form-group mb-3 ${
              shouldShowPasswordConfirm ? 'visible' : ''
            }`}
            controlId='formPasswordConfirm'
          >
            <FloatingLabel
              controlId='floatingPasswordConfirm'
              label='Confirm your password'
            >
              <span
                className='visibility-toggle'
                onClick={() => toggleConfirmPasswordVisibility()}
              >
                {confirmPasswordType === TYPE_PASSWORD ? (
                  <i className='icon bi-eye-slash'></i>
                ) : (
                  <i className='icon bi-eye'></i>
                )}
              </span>
              <Form.Control
                name='password_confirm'
                disabled={!!loading}
                size='lg'
                placeholder='Confirm your password'
                required
                type={confirmPasswordType}
                onChange={e =>
                  setAndValidatePasswordConfirm(e.currentTarget.value)
                }
                onBlur={() => handlePasswordConfirmBlur()}
                onKeyDown={e =>
                  e.key === 'Enter' && handlePasswordConfirmBlur(true)
                }
                isInvalid={!isValidPasswordConfirm}
                isValid={passwordConfirm && isValidPasswordConfirm}
              />
              <Form.Control.Feedback type='invalid'>
                Your passwords do not match
              </Form.Control.Feedback>
              <Form.Control.Feedback>Passwords match 🔐</Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>

          {shouldShowPasswordConfirm && (
            <Row className='mb-3'>
              <Col>
                <Button
                  variant='primary'
                  className='w-100'
                  disabled={password === '' || passwordConfirm === '' || !isValidPassword || !isValidPasswordConfirm}
                  onClick={() => {
                    handleUpdatePassword()
                  }}
                >
                  Update my password
                </Button>
              </Col>
            </Row>
          )}

          <UserAssessmentModal className='my-5' />

          {userPreferences && (
            <Form.Check
              type='switch'
              id='use-remble-anonymously'
              label='Use Remble anonymously'
              checked={useAnonymously}
              onChange={(e) => {
                setUseAnonymously(e.target.checked)
                const updatedUserPreferences = {
                  ...userPreferences,
                  track_user_id: e.target.checked
                }
                saveUserPreferences(updatedUserPreferences)
              }}
            />
          )}

          <AccountDeleteNotice />
        </Col>
      </Row>
    </Container>
  )
}
